import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import { useLanguage } from "../../context/useLanguage";
import TextInput from "../../components/ui/TextInput";
import { createBusinessCatalog, getBusinessCatalogs, getCitiesAdmin } from "../../api/administration";
import { GetBusinessCatalogsResponse, BusinessCatalog } from "../../types/requestsOutputs";
import BusinessCatalogEditModal from "../../components/modals/BusinessCatalogEditModal";
import { City } from "../../types/types";
import FormGroup from "../../components/common/FormGroup";
import { TextField, DropdownField, RadioField } from "../../components/common/FormFields";
import Icon from "../../components/common/Icon";
import styles from "./BusinessCatalogs.module.css";

const isIncompleteNominative = (catalog: BusinessCatalog) =>
    Boolean(catalog.nominatifs && catalog.tracks && catalog.tracks.length < catalog.nbrTracks);

const BusinessCatalogs: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const [businessCatalogs, setBusinessCatalogs] = useState<GetBusinessCatalogsResponse>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>("");
    const [selectedBusinessCatalog, setSelectedBusinessCatalog] = useState<BusinessCatalog | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        fetchBusinessCatalogs();
        fetchCities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const fetchCities = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getCitiesAdmin(token, language);
            setCities(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBusinessCatalogs = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getBusinessCatalogs(token);
            setBusinessCatalogs(response);
        } catch (error) {
            console.error("Failed to fetch businessCatalogs", error);
        } finally {
            setLoading(false);
        }
    };

    const validationSchema = Yup.object({
        newBusinessCatalogName: Yup.string().required(t("enterBusinessCatalogName")),
        chosenCityId: Yup.string().required(t("selectCity")),
        numberTracks: Yup.number().required(t("enterNbrTracks")).min(1),
        nominatifs: Yup.boolean().required(),
    });

    const incompleteNominativeCatalogs = businessCatalogs.filter(isIncompleteNominative);

    const filteredCompleteCatalogs = businessCatalogs
        .filter((catalog) => !isIncompleteNominative(catalog))
        .filter((catalog) => catalog.internalName?.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => (a.internalName && b.internalName ? a.internalName.localeCompare(b.internalName) : 0));

    if (loading) return <div>Chargement...</div>;

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle} ${styles.pageHead}`}>
                <TitleSubtitle
                    title={t("businessCatalogs")}
                    subtitle={t("businessCatalogsSubTitle")}
                    classNameSubtitle={styles.pageSubtitle}
                />
            </div>

            <section className={styles.addSection}>
                <Formik
                    initialValues={{
                        newBusinessCatalogName: "",
                        chosenCityId: "",
                        numberTracks: 1,
                        nominatifs: false,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { resetForm }) => {
                        const token = localStorage.getItem("token");
                        if (!token) return;
                        await createBusinessCatalog(
                            token,
                            values.newBusinessCatalogName,
                            values.chosenCityId,
                            values.numberTracks,
                            values.nominatifs,
                        );
                        resetForm();
                        fetchBusinessCatalogs();
                    }}
                    validateOnMount
                >
                    {({ isSubmitting, errors }) => (
                        <>
                            <h2 className={styles.addTitle}>{t("addNewBusinessCatalog")}</h2>
                            <Form className={styles.addForm}>
                                <FormGroup
                                    name="newBusinessCatalogName"
                                    title={t("enterBusinessCatalogName")}
                                    required
                                    size="small"
                                >
                                    <Field
                                        name="newBusinessCatalogName"
                                        component={TextField}
                                        placeholder={t("enterBusinessCatalogName")}
                                    />
                                </FormGroup>

                                <FormGroup name="chosenCityId" title={t("selectCity")} required size="small">
                                    <Field
                                        name="chosenCityId"
                                        component={DropdownField}
                                        options={cities.map((city) => ({
                                            label: city.officialName || "NA",
                                            value: city.id,
                                        }))}
                                        placeholder={t("selectCity")}
                                        isWithoutMargin={true}
                                    />
                                </FormGroup>

                                <FormGroup
                                    name="numberTracks"
                                    title={t("enterNbrTracks")}
                                    required
                                    size="small"
                                    className={styles.numField}
                                >
                                    <Field
                                        name="numberTracks"
                                        type="number"
                                        component={TextField}
                                        placeholder={t("enterNbrTracks")}
                                    />
                                </FormGroup>

                                <FormGroup title={t("nominatifs")} size="small">
                                    <Field
                                        name="nominatifs"
                                        component={RadioField}
                                        value={true}
                                        label={t("isNominatifTrue")}
                                        subtitle={t("isNotNominatifTrueSubtitle")}
                                    />
                                    <Field
                                        name="nominatifs"
                                        component={RadioField}
                                        value={false}
                                        label={t("isNominatifFalse")}
                                        subtitle={t("isNotNominatifFalseSubtitle")}
                                    />
                                </FormGroup>

                                <button
                                    className={`${adminSurface.addButton} ${styles.addBtn}`}
                                    type="submit"
                                    disabled={isSubmitting || Object.keys(errors).length > 0}
                                >
                                    {t("add")}
                                </button>
                            </Form>
                        </>
                    )}
                </Formik>
            </section>

            {incompleteNominativeCatalogs.length > 0 && (
                <div className="admin-section-gap" style={{ marginTop: 0 }}>
                    <div className={styles.warnTitle}>
                        <Icon name="ic-alert-triangle" className="ic" />
                        {t("incompleteBusinessCatalogs")}
                    </div>
                    <div className={adminSurface.tableCard}>
                        <div className={adminSurface.tblScrollBody}>
                            <table className={adminSurface.table}>
                                <thead>
                                    <tr>
                                        <th>{t("businessCatalogName")}</th>
                                        <th>{t("nominatifs")}</th>
                                        <th>{t("nbrTracks")}</th>
                                        <th>{t("tracksLinked")}</th>
                                        <th>{t("actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {incompleteNominativeCatalogs.map((catalog) => (
                                        <tr key={catalog.id}>
                                            <td>{catalog.internalName}</td>
                                            <td>{catalog.nominatifs ? t("yes") : t("no")}</td>
                                            <td>{catalog.nbrTracks}</td>
                                            <td>{catalog.tracks?.length || 0}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={styles.btnCta}
                                                    onClick={() => {
                                                        setSelectedBusinessCatalog(catalog);
                                                        setShowEditModal(true);
                                                    }}
                                                >
                                                    {t("complete")}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className={adminSurface.tblFoot}>
                            <span>
                                {incompleteNominativeCatalogs.length}{" "}
                                {t("incompleteBusinessCatalogs")?.toLowerCase?.() || "incomplete catalogs"}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <div className={adminSurface.tblBar} style={{ marginTop: 28 }}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchBusinessCatalog")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("businessCatalogName")}</th>
                                <th>{t("nominatifs")}</th>
                                <th>{t("nbrTracks")}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCompleteCatalogs.map((businessCatalog) => (
                                <tr key={businessCatalog.id}>
                                    <td>{businessCatalog.internalName}</td>
                                    <td>{businessCatalog.nominatifs ? t("yes") : t("no")}</td>
                                    <td>{businessCatalog.nbrTracks}</td>
                                    <td>
                                        <div className="cities-table-actions">
                                            <button
                                                type="button"
                                                className={styles.btnMod}
                                                onClick={() => {
                                                    setSelectedBusinessCatalog(businessCatalog);
                                                    setShowEditModal(true);
                                                }}
                                            >
                                                {t("modify")}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredCompleteCatalogs.length} {t("businessCatalogs")?.toLowerCase?.() || "catalogs"}
                    </span>
                </div>
            </div>

            {selectedBusinessCatalog && (
                <BusinessCatalogEditModal
                    businessCatalog={selectedBusinessCatalog}
                    isOpen={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        fetchBusinessCatalogs();
                    }}
                />
            )}
        </div>
    );
};

export default BusinessCatalogs;
